import React, { useMemo, useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { Skeleton } from "./Skeleton";

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  render?: (row: T) => React.ReactNode;
  /** value used for sorting; defaults to row[key]. */
  sortValue?: (row: T) => string | number;
  sortable?: boolean;
  align?: "left" | "right" | "center";
  /** render value in tabular-nums mono (scientific values). */
  numeric?: boolean;
  width?: string;
}

export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  loading = false,
  empty = "No rows.",
  initialSort,
}: {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  empty?: React.ReactNode;
  initialSort?: { key: string; dir: "asc" | "desc" };
}) {
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(
    initialSort ?? null,
  );

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const val = (r: T) =>
      col.sortValue ? col.sortValue(r) : ((r as Record<string, unknown>)[col.key] as string | number);
    const out = [...rows].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av == null) return 1;
      if (bv == null) return -1;
      return av < bv ? -1 : av > bv ? 1 : 0;
    });
    return sort.dir === "desc" ? out.reverse() : out;
  }, [rows, sort, columns]);

  const toggle = (key: string) =>
    setSort((s) =>
      s?.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );

  const alignCls = (a?: string) =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  return (
    <div className="rounded-lg border border-border bg-surface shadow-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-bg text-textLight">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={c.width ? { width: c.width } : undefined}
                className={`px-md py-sm font-medium ${alignCls(c.align)} ${c.sortable ? "cursor-pointer select-none" : ""}`}
                onClick={c.sortable ? () => toggle(c.key) : undefined}
              >
                <span className="inline-flex items-center gap-xs">
                  {c.header}
                  {c.sortable && sort?.key === c.key &&
                    (sort.dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading &&
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={`sk-${i}`} className="border-t border-border">
                {columns.map((c) => (
                  <td key={c.key} className="px-md py-sm"><Skeleton className="h-4 w-full" /></td>
                ))}
              </tr>
            ))}
          {!loading && sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-md py-xl text-center text-textLight">
                {empty}
              </td>
            </tr>
          )}
          {!loading &&
            sorted.map((row) => (
              <tr
                key={getRowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`border-t border-border ${onRowClick ? "cursor-pointer hover:bg-bg" : ""}`}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-md py-sm text-text ${alignCls(c.align)} ${c.numeric ? "font-mono tabular-nums" : ""}`}
                  >
                    {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
